import { addDays } from 'date-fns'

import {
  AbsoluteMinimumsDataProps,
  AbsoluteMinimumsProps,
} from '@/interfaces/absolute-minimums'

import { ABBREVIATION_TO_STATE } from './absolute-minimums-helpers'

export const formatDate = (date: string | undefined) =>
  date?.replace(/(\d{4})(\d{2})(\d{2})/g, '$2/$3/$1')

export const revertDate = (date: string) => {
  return date?.replace(/(\d\d)\/(\d\d)\/(\d{4})/, '$3$1$2')
}

export const formatDateString = (date: string | undefined) => {
  if (!date) return ''
  const d = new Date(date)
  const month = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  const year = d.getUTCFullYear()
  return `${month}/${day}/${year}`
}

export const getExpirationStatus = (effectiveDate: string | undefined) => {
  if (effectiveDate) {
    const expirationDate = new Date(getAgreementExpirationDate(effectiveDate))
    const currentDate = new Date()

    if (currentDate >= expirationDate) {
      return true
    }
    return false
  }
  return false
}

export const getAgreementExpirationDate = (effectiveDate?: string) => {
  if (!effectiveDate) return 'N/A'
  const agreementStartDate = new Date(effectiveDate)

  const expirationDate = new Date(agreementStartDate)
  expirationDate.setFullYear(expirationDate.getFullYear() + 1)
  return expirationDate.toLocaleDateString()
}

export const getNewAgreementAnnualEffectiveDate = (
  activeAgreementExpirationDate?: string
) => {
  if (!activeAgreementExpirationDate) return new Date().toISOString()
  const expirationDate = new Date(activeAgreementExpirationDate)

  return addDays(expirationDate, 1).toISOString().split('T')[0]
}

export const toTitleCase = (string: string) =>
  string
    .replace(/([A-Z])/g, (match) => ` ${match}`)
    .replace(/^./, (match) => match.toUpperCase())
    .trim()

export const sortedObject = (unordered: any) =>
  Object.keys(unordered)
    .sort()
    .reduce(
      (acc, key) => ({
        ...acc,
        [key]: unordered[key],
      }),
      {}
    )

export const getTableColumnsFromLength = (columns: number) => {
  switch (columns) {
    case 1:
      return 'grid-cols-1'
    case 2:
      return 'grid-cols-2'
    case 3:
      return 'grid-cols-3'
    case 4:
      return 'grid-cols-4'
    case 5:
      return 'grid-cols-5'
    case 6:
      return 'grid-cols-6'
    case 7:
      return 'grid-cols-7'
    case 8:
      return 'grid-cols-8'
  }
}

export const getBottomBorder = (length: number, i: number) =>
  i === length - 1 ? 'border-b-0' : 'border-b-2'

export const formatPhoneNumber = (string: string) => {
  const onlyNumber = string.replace(/\D/g, '')
  return onlyNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
}

export const zeroPad = (num: number, places: number) =>
  String(num).padStart(places, '0')

export function compareValuesWithDecimals(
  defaultValue: string,
  value?: string
): boolean {
  if (!value) return false
  const floatValue1 = parseFloat(defaultValue)
  const floatValue2 = parseFloat(value)

  return floatValue1 !== floatValue2
}

export const formatAbsoluteMinimums = (
  data: AbsoluteMinimumsDataProps | undefined
) => {
  if (!data) return []

  const originOrder = ['CO', 'ID', 'MT', 'NE', 'SD', 'UT', 'WA', 'WY']

  const groupedByOrigin: { [key: string]: AbsoluteMinimumsProps } = {}

  data.forEach((item) => {
    const origin = item.origin

    if (!groupedByOrigin[origin]) {
      groupedByOrigin[origin] = {
        origin,
        name:
          ABBREVIATION_TO_STATE[origin as keyof typeof ABBREVIATION_TO_STATE] ??
          origin,
        direct: [],
        partnerDirect: [],
        nonDirect: [],
      }
    }

    const formattedItem = {
      rate: item.rate ?? 0,
      state: item.destination ?? '',
    }

    if (item.isPartnerDirect) {
      groupedByOrigin[origin].partnerDirect.push(formattedItem)
    } else if (item.isDirect) {
      groupedByOrigin[origin].direct.push(formattedItem)
    } else {
      groupedByOrigin[origin].nonDirect.push(formattedItem)
    }
  })

  const groupedArray = Object.values(groupedByOrigin)

  const sortByDestinationState = (array: { state: string }[]) => {
    const aosItem = array.find((item) => item.state === 'AOS')
    const filtered = array.filter((item) => item.state !== 'AOS')

    filtered.sort((a, b) => {
      const indexA = originOrder.indexOf(a.state)
      const indexB = originOrder.indexOf(b.state)

      const aInList = indexA !== -1
      const bInList = indexB !== -1

      if (aInList && bInList) {
        return indexA - indexB
      } else if (aInList) {
        return -1
      } else if (bInList) {
        return 1
      } else {
        return a.state.localeCompare(b.state)
      }
    })

    if (aosItem) filtered.push(aosItem)
    return filtered
  }
  groupedArray.forEach((origin) => {
    origin.direct = sortByDestinationState(origin.direct) as {
      id?: number
      state: string
      rate: number
    }[]
    origin.partnerDirect = sortByDestinationState(origin.partnerDirect) as {
      id?: number
      state: string
      rate: number
    }[]
    origin.nonDirect = sortByDestinationState(origin.nonDirect) as {
      id?: number
      state: string
      rate: number
    }[]
  })

  groupedArray.sort((a, b) => {
    const indexA = originOrder.indexOf(a.origin)
    const indexB = originOrder.indexOf(b.origin)
    return indexA - indexB
  })

  return groupedArray
}

export const toBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
  })
